// Runtime helper functions for catalog log events.
// Split from catalog-log-event.ts to keep the types file focused on shape.

import type { ColorVariant } from '@/theme/tokens';
import type {
  CatalogEventDiscriminator,
  EventOutcome,
  JsonPatchOperation,
  LogEvent,
} from '@/types/data/catalog-log-event';
import {
  DISCRIMINATOR_ENTITY_PATCHED,
  DISCRIMINATOR_ENTITY_REMOVED,
  activityEventLabel,
  spaceLabel,
} from '@/types/data/catalog-log-registry';

// ---------------------------------------------------------------------------
// Outcome / dot color
// ---------------------------------------------------------------------------

const OUTCOME_VARIANT: Record<EventOutcome['result'], ColorVariant> = {
  success: 'success',
  failure: 'error',
  blocked: 'warning',
  partial: 'info',
};

export function outcomeVariant(result: EventOutcome['result']): ColorVariant {
  return OUTCOME_VARIANT[result];
}

const EVENT_DOT_COLOR: Record<LogEvent['kind'], ColorVariant> = {
  entityPatched: 'warning',
  entityRemoved: 'error',
  activity: 'info',
};

const ACTIVITY_OUTCOME_DOT: Record<EventOutcome['result'], ColorVariant> = {
  success: 'success',
  failure: 'error',
  blocked: 'warning',
  partial: 'info',
};

export function eventDotColor(event: LogEvent): ColorVariant {
  if (event.kind === 'activity')
    return ACTIVITY_OUTCOME_DOT[event.outcome.result];
  if (event.kind === 'entityPatched') return dominantPatchColor(event.patch);
  return EVENT_DOT_COLOR[event.kind];
}

// ---------------------------------------------------------------------------
// Patch op lookups
// ---------------------------------------------------------------------------

const PATCH_OP_COLOR: Record<JsonPatchOperation['op'], ColorVariant> = {
  add: 'success',
  remove: 'error',
  replace: 'warning',
  move: 'warning',
  copy: 'warning',
  test: 'neutral',
};

const COLOR_PRIORITY: ColorVariant[] = [
  'error',
  'warning',
  'success',
  'info',
  'neutral',
];

function dominantPatchOp(ops: JsonPatchOperation[]): JsonPatchOperation['op'] {
  return (COLOR_PRIORITY.map(
    (c) => Object.entries(PATCH_OP_COLOR).find(([, v]) => v === c)?.[0]
  )
    .filter(Boolean)
    .find((op) => ops.some((o) => o.op === op)) ??
    ops[0]?.op ??
    'replace') as JsonPatchOperation['op'];
}

function dominantPatchColor(ops: JsonPatchOperation[]): ColorVariant {
  return PATCH_OP_COLOR[dominantPatchOp(ops)];
}

export function eventIconColor(event: LogEvent): ColorVariant {
  if (event.kind === 'entityRemoved') return 'error';
  if (event.kind === 'entityPatched') return dominantPatchColor(event.patch);
  return OUTCOME_VARIANT[event.outcome.result];
}

// ---------------------------------------------------------------------------
// Labels / summaries
// ---------------------------------------------------------------------------

export function eventLabel(event: LogEvent): string {
  if (event.kind === 'entityPatched') return 'Entity Patched';
  if (event.kind === 'entityRemoved') return 'Entity Removed';
  return activityEventLabel(event.metadata.discriminator);
}

/** Format a JSON-patch path into a readable field name: "/department" → "department" */
export function patchFieldName(path: string): string {
  const segments = path.replace(/^\//, '').split('/');
  const raw = segments[segments.length - 1] ?? path;
  return raw.replace(/_/g, ' ');
}

/** Produce a short, descriptive summary of what happened in this event. */
export function eventSummary(event: LogEvent): string {
  if (event.kind === 'entityPatched') {
    const type = event.target.type ? spaceLabel(event.target.type) : 'Entity';
    const ops = event.patch.map((op) => op.op);
    const uniqueOps = new Set(ops);

    const verb =
      uniqueOps.size === 1 && ops[0] === 'add'
        ? 'added'
        : uniqueOps.size === 1 && ops[0] === 'remove'
          ? 'removed'
          : 'updated';
    const fields = [
      ...new Set(event.patch.map((op) => patchFieldName(op.path))),
    ];
    const field = fields.length === 1 && fields[0] ? `${fields[0]} ` : '';
    return `${type} ${field}${verb}`;
  }
  if (event.kind === 'entityRemoved') {
    const type = event.target.type ? spaceLabel(event.target.type) : 'Entity';
    return `${type} removed`;
  }
  // activity
  return eventLabel(event);
}

export function eventSubject(event: LogEvent): string {
  if (event.kind === 'entityPatched' || event.kind === 'entityRemoved') {
    return event.target.displayName ?? '';
  }
  return event.target.displayName ?? event.actor.displayName ?? event.actor.ref;
}

// ---------------------------------------------------------------------------
// Patch counts
// ---------------------------------------------------------------------------

export type PatchCounts = {
  additions: number;
  modifications: number;
  removals: number;
};

export function patchCounts(ops: JsonPatchOperation[]): PatchCounts {
  return {
    additions: ops.filter((op) => op.op === 'add').length,
    modifications: ops.filter(
      (op) => op.op === 'replace' || op.op === 'move' || op.op === 'copy'
    ).length,
    removals: ops.filter((op) => op.op === 'remove').length,
  };
}

// ---------------------------------------------------------------------------
// Severity — only critical-risk events get a visual treatment (red ring).
// The dot color already communicates outcome (success/failure/warning).
// ---------------------------------------------------------------------------

export function eventSeverityStyle(event: LogEvent): string {
  if (event.kind !== 'activity') return '';
  return event.security?.riskLevel === 'critical'
    ? 'ring-1 ring-error bg-error/5'
    : '';
}

// ---------------------------------------------------------------------------
// Patch add refs — junction/relation refs surfaced on add operations
// ---------------------------------------------------------------------------

/** Human-readable labels for known junction ref fields. */
const PATCH_REF_LABEL: Record<string, string> = {
  account_ref: 'Account Ref',
  application_ref: 'Application Ref',
  device_ref: 'Device Ref',
  group_ref: 'Group Ref',
  license_ref: 'License Ref',
  manager_account_ref: 'Manager Account Ref',
  multi_factor_ref: 'Multi Factor Ref',
  permission_ref: 'Permission Ref',
  role_ref: 'Role Ref',
};

export type PatchAddRef = { key: string; label: string; value: string };

/**
 * Extracts named relation refs from patch add operations.
 * Handles both incremental paths (`/group_ref`) and full-document adds
 * (`path: ""` with an object value containing `*_ref` keys).
 */
export function patchAddRefs(patch: JsonPatchOperation[]): PatchAddRef[] {
  const refs: PatchAddRef[] = [];

  for (const op of patch) {
    if (op.op !== 'add') continue;

    if (op.path === '' && op.value !== null && typeof op.value === 'object') {
      // Full-document add — extract *_ref keys from the value object
      const obj = op.value as Record<string, unknown>;
      for (const [key, val] of Object.entries(obj)) {
        const label = PATCH_REF_LABEL[key];
        if (label && typeof val === 'string') {
          refs.push({ key, label, value: val });
        }
      }
    } else {
      // Incremental add — path like "/group_ref"
      const key = op.path.replace(/^\//, '');
      const label = PATCH_REF_LABEL[key];
      if (label && typeof op.value === 'string') {
        refs.push({ key, label, value: op.value });
      }
    }
  }

  return refs;
}

// ---------------------------------------------------------------------------
// Discriminator derivation
// ---------------------------------------------------------------------------

/**
 * Derive the *event-type* discriminator for a log event.
 * Entity events use a fixed discriminator; activity events carry theirs in
 * `metadata.discriminator`.
 */
export function eventDiscriminator(event: LogEvent): CatalogEventDiscriminator {
  if (event.kind === 'entityPatched') return DISCRIMINATOR_ENTITY_PATCHED;
  if (event.kind === 'entityRemoved') return DISCRIMINATOR_ENTITY_REMOVED;
  return event.metadata.discriminator as CatalogEventDiscriminator;
}
